import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {
  ClientTagSchemaConstraint,
  isTagSchemaConstraint,
  SUPPORTED_UNIQUE_RESIDUES_CONSTRAINTS,
  TagSchemaConstraint,
  UNIQUE_CANONICAL_SMILES_CONSTRAINT,
} from 'constants/RegistryConstants';
import {TagSchemaConstraintStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import FieldTableHeader from 'registry/field/FieldTableHeader';
import {schemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';
import ComputingTagSchemaConstraint from 'settings/registry/ComputingTagSchemaConstraint';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import TagSchemaConstraintComponent from './TagSchemaConstraint';

export enum ConstraintTableType {
  INTERFACE = 'interface',
  SCHEMA = 'schema',
}

export interface ConstraintTableProps {
  constraint?: ClientTagSchemaConstraint | TagSchemaConstraint | null;
  folderItemType: string | null;
  onAddClick: () => void;
  onRemoveClick: () => void;
  writable: boolean;
  tableType: ConstraintTableType;
}

function getConstraintFields(
  constraint: ClientTagSchemaConstraint | TagSchemaConstraint,
  folderItemType: string | null
): Array<string> {
  const fields: Array<string> = constraint.fields.map((field) => field.displayName || field.name);
  if (constraint.uniqueResidues) {
    const uniqueResiduesConstraint = _.findWhere(SUPPORTED_UNIQUE_RESIDUES_CONSTRAINTS, {
      folderItemType,
      areUniqueResiduesCaseSensitive: constraint.areUniqueResiduesCaseSensitive,
    });
    assert(uniqueResiduesConstraint != null, 'Expected non-null `uniqueResiduesConstraint`');
    fields.unshift(uniqueResiduesConstraint.text);
  }
  if (constraint.uniqueCanonicalSmiles) {
    fields.unshift(UNIQUE_CANONICAL_SMILES_CONSTRAINT.text);
  }
  return fields;
}

function getActiveOrClientConstraint(
  constraint: ClientTagSchemaConstraint | TagSchemaConstraint | null | undefined
): ClientTagSchemaConstraint | TagSchemaConstraint | null {
  if (
    !constraint ||
    (isTagSchemaConstraint(constraint) && constraint.status !== TagSchemaConstraintStatus.ACTIVE)
  ) {
    return null;
  }
  return constraint;
}

function getComputingConstraint(
  constraint: ClientTagSchemaConstraint | TagSchemaConstraint | null | undefined
): TagSchemaConstraint | null {
  return constraint &&
    isTagSchemaConstraint(constraint) &&
    constraint.status === TagSchemaConstraintStatus.COMPUTING
    ? constraint
    : null;
}

export default function ConstraintTable({
  constraint,
  folderItemType,
  onAddClick,
  onRemoveClick,
  writable,
  tableType,
}: ConstraintTableProps): JSX.Element {
  const activeOrClientConstraint = getActiveOrClientConstraint(constraint);
  const computingConstraint = getComputingConstraint(constraint);

  const constraintSource =
    (isTagSchemaConstraint(activeOrClientConstraint) && activeOrClientConstraint.source) || null;
  return (
    <div
      className="simpleDivTable u-large-spacing-bottom"
      {...getTestProps({component: 'ConstraintTable', element: 'root'})}
    >
      <FieldTableHeader
        isDisabled={Boolean(!writable || activeOrClientConstraint || computingConstraint)}
        onAddField={onAddClick}
        shouldShowTooltip={false}
        title="Unique Constraint"
        helpText={
          tableType === ConstraintTableType.INTERFACE
            ? 'The uniqueness constraint ensures each item has a unique set of values for the specified fields, across all schemas using this fieldset.'
            : undefined
        }
      />
      <div className="simpleDivTable-rowContainer">
        <a
          className={classNames('faded u-danger-pointer simpleDivTable-action simpleDivTable-action--move', {
            invisible: Boolean(!writable || !activeOrClientConstraint),
          })}
          onClick={onRemoveClick}
        >
          <BenchlingIcon icon="minus-large" />
        </a>
        {activeOrClientConstraint ? (
          <TagSchemaConstraintComponent
            fields={getConstraintFields(activeOrClientConstraint, folderItemType)}
            source={constraintSource}
            tableType={tableType}
          />
        ) : computingConstraint ? (
          <ComputingTagSchemaConstraint />
        ) : (
          <div
            className="simpleDivTable-row"
            {...getTestProps({component: 'EmptyConstraintTableRow', element: 'root'})}
          >
            <div className="simpleDivTable-cell simpleDivTable-cell--emptyMessage">
              This {tableType === ConstraintTableType.INTERFACE ? schemaFieldsetName() : 'schema'} does not
              have a constraint
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
